import { useEffect } from "react";

/**
 * Global v2 keyboard shortcuts.
 *
 *   cmd+k (mac) / ctrl+k (win/linux) → toggle command palette
 *   cmd+n (mac) / ctrl+n (win/linux) → dispatch workspace:new-chat event (handled by ProjectView when present)
 *
 * macOS reserves ctrl+<letter> for system-wide readline/emacs bindings
 * (ctrl+k = kill to end of line, ctrl+n = next line, etc.) that are distinct
 * from cmd+<letter>. We never alias ctrl to cmd there — only cmd is our
 * modifier on Mac. Windows/Linux have no cmd key, so ctrl is the modifier
 * for our shortcuts there instead.
 *
 * Shortcuts that conflict with native browser behavior in editable inputs
 * (textarea / contenteditable / input) are suppressed there, except for
 * the palette trigger, which always opens the palette regardless of focus.
 */

const isMac = () => /Mac|iPhone|iPod|iPad/i.test(navigator.platform || navigator.userAgent || "");

const isMod = (e) => (isMac() ? e.metaKey : e.ctrlKey);

const isEditable = (el) => {
  if (!el) return false;
  const tag = el.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA") return true;
  if (el.isContentEditable) return true;
  return false;
};

export const useKeyboardShortcuts = ({ onCommandPalette, onNewChat }) => {
  useEffect(() => {
    const handler = (e) => {
      const key = e.key?.toLowerCase();
      if (isMod(e) && key === "k") {
        e.preventDefault();
        onCommandPalette?.();
        return;
      }
      if (isMod(e) && key === "n" && !isEditable(document.activeElement)) {
        e.preventDefault();
        onNewChat?.();
        return;
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onCommandPalette, onNewChat]);
};
